<template>
  <div class="d-flex flex-column">
    <div class="grid-header">
      <h1>{{ $t("cliente_operaciones.titulo") }} {{ props.templateData.data.NombreCompleto }}</h1>
      <div class="toolbar">
      </div>
    </div>

    <DxDataGrid v-bind="getDefaultGridDetailProperties()" :data-source="gridSource" ref="grid"
      class="dx-card wide-card grid-operaciones">
      <DxColumn data-field="CodigoSecuencial" :caption="$t('cliente_operaciones.grid.id')" format="0000000000">
      </DxColumn>
      <DxColumn v-if="puedoVerAgente" data-field="NombreAgente" :caption="$t('cliente_operaciones.grid.agente')">
      </DxColumn>
      <DxColumn data-field="Comercializadora" :caption="$t('cliente_operaciones.grid.comercializadora')"></DxColumn>
      <DxColumn data-field="TelefonoMovil" :caption="$t('cliente_operaciones.grid.telefono_movil')"></DxColumn>
      <DxColumn data-field="TelefonoFijo" :caption="$t('cliente_operaciones.grid.telefono_fijo')"></DxColumn>
      <DxColumn data-field="Email" :caption="$t('cliente_operaciones.grid.email')"></DxColumn>
      <DxColumn data-field="TipoPersona" :caption="$t('cliente_operaciones.grid.tipo_persona')"
        cell-template="tipoPersonaTemplate">
        <DxLookup v-bind="getLookup('cliente_operaciones', 'tipo_persona')"></DxLookup>
      </DxColumn>
      <DxColumn data-field="Direccion" :caption="$t('cliente_operaciones.grid.direccion')"></DxColumn>
      <DxColumn data-field="TarifaAccesoLuz" :caption="$t('cliente_operaciones.grid.tarifa_de_acceso_luz')"></DxColumn>
      <DxColumn data-field="CupsLuz" :caption="$t('cliente_operaciones.grid.cups_luz')"></DxColumn>
      <DxColumn data-field="TarifaAccesoGas" :caption="$t('cliente_operaciones.grid.tarifa_de_acceso_gas')"></DxColumn>
      <DxColumn data-field="CupsGas" :caption="$t('cliente_operaciones.grid.cups_gas')"></DxColumn>
      <DxColumn data-field="CodigoEstado" :caption="$t('cliente_operaciones.grid.estado')"
        cell-template="estadoTemplate">
        <DxLookup v-bind="getLookup('operaciones', 'estado')"></DxLookup>
      </DxColumn>
      <DxColumn v-if="puedoVerCoste" data-field="Retrocomision" :caption="$t('operaciones.grid.retrocomision')"
        data-type="number" :calculate-cell-value="formatRetrocomision" :calculate-filter-expression="getRetrocomisionFilter">
        <DxFormat v-bind="currencyFormat()"></DxFormat>
      </DxColumn>
      <DxColumn v-if="puedoVerCoste" data-field="Comision" :caption="$t('cliente_operaciones.grid.comision')"
        data-type="number">
        <DxFormat v-bind="currencyFormat()"></DxFormat>
      </DxColumn>
      <DxColumn type="buttons" :fixed="true"
        fixed-position="right" :buttons="[
          {
            cssClass: 'btn btn-link text-decoration-none btn-sm',
            icon: 'fa-solid fa-magnifying-glass text-success',
            hint: $t('clientes_operaciones.grid.ver_detalle'),
            onClick: (e: any) => verDetalle(e)
          }
        ]"/>
      <DxColumnFixing :enabled="true" />

      <template #estadoTemplate="{ data }">
        <div :class="[
          `d-inline-block rounded py-1 px-2`,
          `estado-operacion ${data.value.replace(' ', '')}`,
        ]">
            {{ $t(`operaciones.estado.${data.value}`) }}
        </div>
      </template>
      <template #tipoPersonaTemplate="{ data }">
        <div class="d-flex">
          {{ $t(`cliente_operaciones.tipo_persona.${data.value}`) }}
        </div>
      </template>
      <DxMasterDetail :enabled="true" template="masterDetailTemplate" />
      <template #masterDetailTemplate="{ data: operacion }">
        <operaciones-grid-detail :template-data="operacion" />
      </template>
    </DxDataGrid>
  </div>
</template>

<script setup lang="ts">
import { getDefaultGridDetailProperties, getLookup, currencyFormat, getRetrocomisionFilter } from '@/.shared/helpers/grid-helper';
import { DxDataGrid, DxColumn, type DxDataGridTypes, DxMasterDetail, DxColumnFixing, DxFormat, DxLookup } from 'devextreme-vue/data-grid';
import { useApi } from "@api/integration";
import type CustomStore from "devextreme/data/custom_store";
import { computed, onMounted, ref } from "vue";
import type { DXLoadOptions } from '@/.shared/.api';
import type DevExpress from 'devextreme';
import { useAuthorization } from '@/.shared/services/auth/authorization';
import { PermissionsApp } from '@/.shared/services/auth/authorization/authorization-service-impl';
import { useIntl } from '@/.shared/services/i18n';
import { OperacionEnergia } from '@/components/nueva-venta/models/OperacionEnergia';
import OperacionesResumen from '@/components/operaciones-energia/OperacionesEnergiaResumen.vue';
import { useModalService } from '@/.shared/services/modal-service';

const props = defineProps<{
  templateData: DxDataGridTypes.MasterDetailTemplateData;
}>();

const api = useApi();
const gridSource = ref<CustomStore>();
const grid = ref<DxDataGrid>();
const autho = useAuthorization();
const intl = useIntl();
const modal = useModalService();

onMounted(async () => {
  gridSource.value = api.apiStore({
    key: "Id",
    load: async (p) => {
      const q = p as DXLoadOptions;

      if (q.filter != null && q.filter.length >= 0)
        q.filter = [["IdCliente", "=", props.templateData.key], "and", q.filter];
      else
        q.filter = ["IdCliente", "=", props.templateData.key];

      return await api.callStandardApi("getOperacionesEnergiaAgenteForGrid", { showLoading: false }, q)
    }
  });
});

async function verDetalle(data: DevExpress.ui.dxDataGrid.ColumnButtonClickEvent) {
  let title = `${intl.i18n.t("operaciones.detalle.titulo")} ${data.row?.data.CodigoSecuencial.toString().padStart(10, '0')}`;

  var response = await api.callStandardApi("getOperacionEnergiaDetail", { showLoading: true }, data.row?.data.Id);
  var model = new OperacionEnergia();
  model.createFromOperacionDetail(response);

  await modal.showModal(OperacionesResumen, { data: model }, { title: title });
}

const puedoVerCoste = computed(() => {
  return autho.hasPermission(PermissionsApp.ViewDatosEconomicosOperaciones);
});

const puedoVerAgente = computed(() => {
  return autho.hasPermission(PermissionsApp.ViewSameCompanyClientesOperaciones);
})

const formatRetrocomision = (rowData: any) => {
  return rowData.Retrocomision ? -Math.abs(rowData.Retrocomision) : rowData.Retrocomision;
};

</script>
